import { createPortal } from 'react-dom';
import { useEffect, useState } from 'react';
import { LucideIcon } from 'lucide-react';
import { GlassPanel } from './GlassPanel';

export interface MenuDropdownItem {
  id: string;
  title: string;
  sub?: string;
  meta?: string;
  icon: LucideIcon;
  onSelect?: () => void;
}

interface MenuDropdownProps {
  items: MenuDropdownItem[];
  /** The button that spawned this dropdown to calculate portal coordinates. */
  anchorEl?: HTMLElement | null;
  /** Called when the user picks an item or presses Escape. */
  onClose?: () => void;
}

/**
 * Floating glass flyout used by TopMenu. Renders via a React Portal
 * appended to document.body, so the dropdown's `.lg-mirror` is NOT
 * nested inside the top menu's GlassPanel. That nesting would put the
 * dropdown's mirror inside a container with its own filter chain,
 * making the displacement read off the wrong source.
 */
export function MenuDropdown({ items, anchorEl, onClose }: MenuDropdownProps) {
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!anchorEl) return;
    const updatePosition = () => setRect(anchorEl.getBoundingClientRect());
    updatePosition();

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, { passive: true });
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [anchorEl]);

  if (!rect) return null;

  // Centered horizontally on the anchor button via pure pixel math.
  // We CANNOT use `transform: translateX(-50%)` on this wrapper or any
  // ancestor of the GlassPanel — `transform` defines a new containing
  // block which makes `background-attachment: fixed` (used by the
  // .lg-mirror layer to align with the page bg in viewport coords)
  // resolve against the wrapper instead of the viewport. The mirror
  // would then NOT line up with what's behind the dropdown, and the
  // displacement wouldn't refract the right pixels.
  //
  // The dropdown's min-width is 280px (matches CSS .menu-dropdown).
  // We center by setting `left` to the anchor's center minus half the
  // dropdown width.
  const DROPDOWN_W = 280;
  return createPortal(
    <div
      className="menu-dropdown-wrap"
      data-menu-portal="true"
      style={{
        position: 'fixed',
        top: rect.bottom + 12,
        left: rect.left + rect.width / 2 - DROPDOWN_W / 2,
        width: DROPDOWN_W,
        zIndex: 60,
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose?.();
      }}
    >
      <GlassPanel
        variant="menu"
        className="menu-dropdown"
        animate={false}
        role="menu"
      >
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              className="menu-item"
              role="menuitem"
              onClick={() => {
                item.onSelect?.();
                onClose?.();
              }}
            >
              <span className="menu-icon">
                <Icon size={14} strokeWidth={2} />
              </span>
              <span>
                <span className="menu-title">{item.title}</span>
                {item.sub && <span className="menu-sub">{item.sub}</span>}
              </span>
              {item.meta && <span className="menu-meta">{item.meta}</span>}
            </button>
          );
        })}
      </GlassPanel>
    </div>,
    document.body
  );
}
